import { useEffect, useRef, useState } from 'react'
import { previewMailCampaign } from '../../services/adminApi'
import { senderName } from './sender'

interface EmailPreviewProps {
  campaignId: number
  subject: string
  fromEmail: string
  // undefined renders the saved draft; a string renders unsaved editor content.
  body?: string
  templateId?: number | null
  compact?: boolean
}

export function EmailPreview({ campaignId, subject, fromEmail, body, templateId, compact = false }: EmailPreviewProps) {
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [height, setHeight] = useState(compact ? 420 : 640)
  const frameRef = useRef<HTMLIFrameElement>(null)
  const requestId = useRef(0)

  useEffect(() => {
    const id = ++requestId.current
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await previewMailCampaign(campaignId, {
          ...(body !== undefined ? { body } : {}),
          ...(templateId ? { template_id: templateId } : {}),
        })
        if (id !== requestId.current) return
        if (res.ok && res.data) {
          setHtml(res.data.html)
          setError(null)
        } else {
          setError(res.error?.message || 'Preview failed')
        }
      } catch {
        if (id === requestId.current) setError('Preview failed')
      } finally {
        if (id === requestId.current) setLoading(false)
      }
    }, body === undefined ? 0 : 450)
    return () => clearTimeout(timer)
  }, [campaignId, body, templateId])

  const fitHeight = () => {
    const doc = frameRef.current?.contentDocument
    if (doc?.body) setHeight(Math.max(compact ? 320 : 480, doc.documentElement.scrollHeight))
  }

  const sender = senderName(fromEmail)

  return (
    <div className="overflow-hidden rounded-2xl border border-border-card dark:border-[#3d2c4f] bg-white dark:bg-[#141017] shadow-card">
      <div className="flex items-center gap-3 border-b border-gray-100 dark:border-[#2a2133] px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#503864] font-heading text-base font-bold text-white">
          {sender.name.charAt(0).toUpperCase() || 'B'}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate font-semibold text-gray-900 dark:text-gray-100">{sender.name}</span>
            <span className="truncate text-xs text-gray-400">{sender.address}</span>
          </div>
          <p className="truncate text-sm text-gray-600 dark:text-gray-300">{subject.trim() || 'No subject yet'}</p>
        </div>
        {loading && <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[#9a6bb8] border-t-transparent" />}
      </div>
      {error ? (
        <p className="p-6 text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : html === null ? (
        <div className="flex items-center justify-center" style={{ height: compact ? 320 : 480 }}>
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#9a6bb8] border-t-transparent" />
        </div>
      ) : (
        // allow-same-origin without allow-scripts lets us size the frame while nothing inside can run.
        <iframe
          ref={frameRef}
          title="Email preview"
          sandbox="allow-same-origin"
          srcDoc={html}
          onLoad={fitHeight}
          className="block w-full bg-white"
          style={{ height }}
        />
      )}
    </div>
  )
}
